import { observer } from 'mobx-react-lite'
import React, { useCallback, useState } from 'react'
import { Image, StyleSheet, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'

import { useFocusEffect } from 'expo-router'

import { DS } from '../../design-system'
import { useStore } from '../../models'
import { color } from '../../theme'
import CaretRightIcon from './assets/Caret-right.svg'
import NextModuleIcon from './assets/next-module.svg'

export interface NextModuleScreenProps {
  onClose: () => void
  currentModuleId: number
  setZogoModuleId: (moduleId: number) => void
  resetCurrentModule: () => void
}

export const NextModuleScreen = observer((props: NextModuleScreenProps) => {
  const { onClose, currentModuleId, setZogoModuleId, resetCurrentModule } = props
  const {
    zogo: { getZogoToken, getLearningModulesAndStats, learningModulesAndStats }
  } = useStore()
  const [nextModuleName, setNextModuleName] = useState('')
  const getNextModuleData = useCallback(() => {
    if (learningModulesAndStats) {
      const currentModuleIndex = learningModulesAndStats.findIndex(
        (obj) => obj.learningModule?.moduleId === currentModuleId
      )
      if (currentModuleIndex !== learningModulesAndStats.length - 1) {
        const nextModuleData = learningModulesAndStats[currentModuleIndex + 1]
        const moduleName = nextModuleData.learningModule?.moduleTitle
        console.log('Next module name: ', moduleName)
        if (moduleName) {
          setNextModuleName(moduleName)
        }
        if (nextModuleData?.learningModule?.moduleId) {
          setZogoModuleId(nextModuleData.learningModule?.moduleId)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learningModulesAndStats, setZogoModuleId])

  useFocusEffect(
    useCallback(() => {
      getZogoToken()
      getLearningModulesAndStats()
      getNextModuleData()
    }, [getLearningModulesAndStats, getNextModuleData, getZogoToken])
  )

  const proceedToNextModule = () => {
    resetCurrentModule()
  }

  return (
    <SafeAreaView style={styles.screenContainer}>
      <DS.Frame />
      <DS.Frame gap="16" alignItems="center" justifyContent="center">
        <NextModuleIcon />
        <DS.Text textAlign="center" variant="Heading 1/Medium">
          {nextModuleName ? 'Keep going!' : 'Way to go!'}
        </DS.Text>
        {nextModuleName ? (
          <DS.Frame gap="12" style={styles.nextModuleContainer}>
            <DS.Text variant="Heading 5/Medium">Next module</DS.Text>
            <DS.Frame row style={styles.nextModuleData}>
              <View style={styles.nextModuleLeftContainer}>
                <Image
                  source={{ uri: 'https://assets.ppwr.io/wp-content/uploads/2024/11/04234132/Icon%403x-7.png' }}
                  resizeMode="contain"
                  style={styles.learnIcon}
                />
              </View>
              <View style={styles.nextModuleCenterContainer}>
                <DS.Text variant="Body Small/Regular">Review</DS.Text>
                <DS.Text variant="Body Medium/Medium">{nextModuleName}</DS.Text>
              </View>
              <View style={styles.nextModuleRightContainer}>
                <CaretRightIcon />
              </View>
            </DS.Frame>
          </DS.Frame>
        ) : (
          <DS.Frame gap="12" style={{ paddingHorizontal: 24 }}>
            <DS.Text variant="Body Medium/Regular" textAlign="center">
              You’ve mastered all the basics. Now it’s time to take the next step. Grab a seat at a poker table and let
              your skills shine.
            </DS.Text>
          </DS.Frame>
        )}
      </DS.Frame>
      <DS.Frame gap="16" style={{ marginHorizontal: 20 }}>
        {nextModuleName ? (
          <React.Fragment>
            <DS.Button
              variant={'Large/Primary'}
              type={'On Light'}
              backgroundColor={'Brand/Purple'}
              onPress={proceedToNextModule}
              disabled={nextModuleName === ''}
            >
              Next module
            </DS.Button>
            <DS.Button
              variant={'Large/Primary'}
              type={'On Dark'}
              backgroundColor={'Brand/Cream'}
              borderColor={'Brand/Light Grey'}
              onPress={onClose}
            >
              Close
            </DS.Button>
          </React.Fragment>
        ) : (
          <DS.Button
            variant={'Large/Primary'}
            type={'On Light'}
            backgroundColor={'Brand/Purple'}
            borderColor={'Brand/Light Grey'}
            onPress={onClose}
          >
            Close
          </DS.Button>
        )}
      </DS.Frame>
    </SafeAreaView>
  )
})

const styles = StyleSheet.create({
  screenContainer: {
    flex: 1,
    backgroundColor: color.palette.cream,
    paddingHorizontal: 20,
    justifyContent: 'space-between'
  },
  nextModuleContainer: {
    marginHorizontal: 20,
    width: '100%',
    padding: 15,
    borderRadius: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.50)'
  },
  nextModuleData: {
    padding: DS.spacing[16],
    backgroundColor: color.palette.white,
    borderRadius: 8,

    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.23,
    shadowRadius: 2.62,
    elevation: 4
  },
  nextModuleLeftContainer: {
    flex: 1.5,
    justifyContent: 'center',
    alignItems: 'center'
  },
  nextModuleCenterContainer: {
    flex: 8,
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingHorizontal: 10
  },
  nextModuleRightContainer: {
    flex: 0.5,
    justifyContent: 'center',
    alignItems: 'flex-end'
  },
  learnIcon: {
    width: 50,
    height: 50
  }
})
